'use client';

import { c as _c } from "react/compiler-runtime";
import type { ReactNode, Ref } from 'react';
import { useEffect, useImperativeHandle, useRef, useState } from 'react';
import { buildEarlyAccessEnterPayload, buildEarlyAccessPlaybackPayload, buildEarlyAccessSpotlightClearPayload, buildEarlyAccessSpotlightPayload, isEarlyAccessAnimationPayload, isEarlyAccessReadyPayload, isEmbeddedGateSpotlightActive } from '@/components/early-access-gate/early-access-spotlight-bridge';
import { useCursorSpotlight } from '@/hooks/ui/use-cursor-spotlight';
import { useHydrated } from '@/hooks/ui/use-hydrated';
import { useMediaQuery } from '@/hooks/ui/use-media-query';
import { cn } from '@/lib/ui/utils';
const EMBEDDED_SPOTLIGHT_MIN_WIDTH_PX = 1024;
const GATE_VISIBILITY_THRESHOLD = 0.2;
const ANIMATION_IDLE_TIMEOUT_MS = 2_000;
const IFRAME_CLASS = 'absolute inset-0 h-full w-full border-0 motion-reduce:hidden';
function postPlaybackToIframe(iframe: HTMLIFrameElement | null, playing: boolean): void {
  const iframeWindow = iframe?.contentWindow;
  if (iframeWindow === undefined || iframeWindow === null) {
    return;
  }
  iframeWindow.postMessage(buildEarlyAccessPlaybackPayload(playing), window.location.origin);
}
export interface EarlyAccessGateAnimationHandle {
  enter: () => void;
}
interface EarlyAccessGateBackgroundProps {
  /** Skip the 375KB canvas iframe during the brief Statsig/localStorage splash. */
  showAnimation?: boolean;
  /** Hero panel bleed — side fades + hide replay hint in the iframe asset. */
  embedded?: boolean;
  animationRef?: Ref<EarlyAccessGateAnimationHandle | null>;
  onRevealStart?: () => void;
  onRevealComplete?: () => void;
}

/** Canvas animation + vignette layers matching Figma Web-app node 4089:93. */
function buildBackgroundSrc(embedded: boolean): string {
  return embedded ? '/assets/early-access-gate-background.html?embedded=1' : '/assets/early-access-gate-background.html';
}
function useAnimationReady(enabled, start) {
  const $ = _c(4);
  const [ready, setReady] = useState(false);
  let t0;
  let t1;
  if ($[0] !== enabled || $[1] !== start) {
    t0 = () => {
      if (!enabled) {
        return;
      }
      if (start === "next-frame") {
        const frameId = window.requestAnimationFrame(() => setReady(true));
        return () => window.cancelAnimationFrame(frameId);
      }
      if (typeof window.requestIdleCallback === "function") {
        const idleId = window.requestIdleCallback(() => setReady(true), {
          timeout: ANIMATION_IDLE_TIMEOUT_MS
        });
        return () => window.cancelIdleCallback(idleId);
      }
      const timeoutId = window.setTimeout(() => setReady(true), ANIMATION_IDLE_TIMEOUT_MS);
      return () => window.clearTimeout(timeoutId);
    };
    t1 = [enabled, start];
    $[0] = enabled;
    $[1] = start;
    $[2] = t0;
    $[3] = t1;
  } else {
    t0 = $[2];
    t1 = $[3];
  }
  useEffect(t0, t1);
  return enabled && ready;
}
function GateAnimationIframe(t0) {
  const $ = _c(9);
  const {
    iframeRef,
    src,
    embedded,
    onLoad
  } = t0;
  const [painted, setPainted] = useState(false);
  let t1;
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = () => {
      const onMessage = event => {
        if (event.origin !== window.location.origin) {
          return;
        }
        if (isEarlyAccessReadyPayload(event.data)) {
          setPainted(true);
        }
      };
      window.addEventListener("message", onMessage);
      return () => {
        window.removeEventListener("message", onMessage);
      };
    };
    t2 = [];
    $[0] = t1;
    $[1] = t2;
  } else {
    t1 = $[0];
    t2 = $[1];
  }
  useEffect(t1, t2);
  const t3 = embedded && painted === false && "opacity-0";
  let t4;
  if ($[2] !== t3) {
    t4 = cn(IFRAME_CLASS, t3);
    $[2] = t3;
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  let t5;
  if ($[4] !== iframeRef || $[5] !== onLoad || $[6] !== src || $[7] !== t4) {
    t5 = <iframe ref={iframeRef} title="Decorative background animation" src={src} sandbox="allow-scripts allow-same-origin" className={t4} tabIndex={-1} onLoad={onLoad} />;
    $[4] = iframeRef;
    $[5] = onLoad;
    $[6] = src;
    $[7] = t4;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  return t5;
}
export function EarlyAccessGateBackground(t0) {
  const $ = _c(34);
  const {
    showAnimation: t1,
    embedded: t2,
    animationRef,
    onRevealStart,
    onRevealComplete
  } = t0;
  const showAnimation = t1 === undefined ? true : t1;
  const embedded = t2 === undefined ? false : t2;
  const hostRef = useRef(null);
  const iframeRef = useRef(null);
  const mounted = useHydrated();
  const reducedMotion = useMediaQuery("(prefers-reduced-motion: reduce)");
  const animationReady = useAnimationReady(mounted && showAnimation && !reducedMotion, embedded ? "next-frame" : "idle");
  const embeddedLayoutWide = useMediaQuery(`(min-width: ${EMBEDDED_SPOTLIGHT_MIN_WIDTH_PX}px)`);
  const embeddedSpotlightEnabled = embedded && embeddedLayoutWide;
  const [isInView, setIsInView] = useState(true);
  const isInViewRef = useRef(true);
  const pendingSpotlightRef = useRef(undefined);
  const spotlightRafRef = useRef(0);
  const onRevealStartRef = useRef(onRevealStart);
  const onRevealCompleteRef = useRef(onRevealComplete);
  let t3;
  let t4;
  if ($[0] !== onRevealComplete || $[1] !== onRevealStart) {
    t3 = () => {
      onRevealStartRef.current = onRevealStart;
      onRevealCompleteRef.current = onRevealComplete;
    };
    t4 = [onRevealStart, onRevealComplete];
    $[0] = onRevealComplete;
    $[1] = onRevealStart;
    $[2] = t3;
    $[3] = t4;
  } else {
    t3 = $[2];
    t4 = $[3];
  }
  useEffect(t3, t4);
  let t5;
  if ($[4] !== embedded) {
    const flushSpotlight = function flushSpotlight() {
      spotlightRafRef.current = 0;
      const pending = pendingSpotlightRef.current;
      if (pending === undefined) {
        return;
      }
      const iframeWindow = iframeRef.current?.contentWindow;
      if (iframeWindow === undefined || iframeWindow === null) {
        return;
      }
      if (pending === null) {
        iframeWindow.postMessage(buildEarlyAccessSpotlightClearPayload(), window.location.origin);
        return;
      }
      const rect = hostRef.current?.getBoundingClientRect();
      if (rect === undefined) {
        return;
      }
      const clientX = rect.left + pending.x;
      const clientY = rect.top + pending.y;
      const payload = buildEarlyAccessSpotlightPayload(clientX, clientY, embedded ? rect : undefined);
      iframeWindow.postMessage(payload, window.location.origin);
    };
    t5 = function queueSpotlight(position) {
      pendingSpotlightRef.current = position;
      if (spotlightRafRef.current === 0) {
        spotlightRafRef.current = requestAnimationFrame(flushSpotlight);
      }
    };
    $[4] = embedded;
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  const queueSpotlight = t5;
  let t6;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = () => ({
      enter: () => {
        const iframeWindow_0 = iframeRef.current?.contentWindow;
        if (iframeWindow_0 === undefined || iframeWindow_0 === null) {
          onRevealStartRef.current?.();
          onRevealCompleteRef.current?.();
          return;
        }
        iframeWindow_0.postMessage(buildEarlyAccessEnterPayload(), window.location.origin);
      }
    });
    $[6] = t6;
  } else {
    t6 = $[6];
  }
  useImperativeHandle(animationRef, t6);
  let t7;
  let t8;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = () => {
      const onMessage = event => {
        if (event.origin !== window.location.origin) {
          return;
        }
        if (!isEarlyAccessAnimationPayload(event.data)) {
          return;
        }
        if (event.data.event === "reveal-start") {
          onRevealStartRef.current?.();
          return;
        }
        onRevealCompleteRef.current?.();
      };
      window.addEventListener("message", onMessage);
      return () => {
        window.removeEventListener("message", onMessage);
      };
    };
    t8 = [];
    $[7] = t7;
    $[8] = t8;
  } else {
    t7 = $[7];
    t8 = $[8];
  }
  useEffect(t7, t8);
  let t10;
  let t9;
  if ($[9] !== embedded || $[10] !== mounted || $[11] !== reducedMotion || $[12] !== showAnimation) {
    t9 = () => {
      if (!mounted || !showAnimation || reducedMotion) {
        return;
      }
      const host = hostRef.current;
      if (host === null) {
        return;
      }
      const syncPlayback = playing => {
        isInViewRef.current = playing;
        setIsInView(playing);
        postPlaybackToIframe(iframeRef.current, playing);
        if (!playing) {
          pendingSpotlightRef.current = undefined;
          const iframeWindow_1 = iframeRef.current?.contentWindow;
          if (iframeWindow_1 !== undefined && iframeWindow_1 !== null) {
            iframeWindow_1.postMessage(buildEarlyAccessSpotlightClearPayload(), window.location.origin);
          }
        }
      };
      if (typeof IntersectionObserver === "undefined") {
        syncPlayback(true);
        return;
      }
      const observer = new IntersectionObserver(entries => {
        for (const entry of entries) {
          if (entry.target !== host) {
            continue;
          }
          syncPlayback(entry.isIntersecting);
        }
      }, {
        threshold: embedded ? 0 : GATE_VISIBILITY_THRESHOLD
      });
      observer.observe(host);
      return () => {
        observer.disconnect();
      };
    };
    t10 = [embedded, mounted, reducedMotion, showAnimation];
    $[9] = embedded;
    $[10] = mounted;
    $[11] = reducedMotion;
    $[12] = showAnimation;
    $[13] = t10;
    $[14] = t9;
  } else {
    t10 = $[13];
    t9 = $[14];
  }
  useEffect(t9, t10);
  let t11;
  let t12;
  if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = () => () => {
      const rafId = spotlightRafRef.current;
      if (rafId !== 0) {
        cancelAnimationFrame(rafId);
        spotlightRafRef.current = 0;
      }
    };
    t12 = [];
    $[15] = t11;
    $[16] = t12;
  } else {
    t11 = $[15];
    t12 = $[16];
  }
  useEffect(t11, t12);
  const t13 = mounted && showAnimation && !reducedMotion && isInView && (!embedded || embeddedSpotlightEnabled);
  let t14;
  if ($[17] !== embedded) {
    t14 = embedded ? {
      isActiveAt: isEmbeddedGateSpotlightActive
    } : {};
    $[17] = embedded;
    $[18] = t14;
  } else {
    t14 = $[18];
  }
  let t15;
  if ($[19] !== t13 || $[20] !== t14) {
    t15 = {
      enabled: t13,
      ...t14
    };
    $[19] = t13;
    $[20] = t14;
    $[21] = t15;
  } else {
    t15 = $[21];
  }
  useCursorSpotlight(hostRef, queueSpotlight, t15);
  let t16;
  if ($[22] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = <div className="absolute inset-0 bg-[radial-gradient(circle,color-mix(in_oklch,var(--foreground-primary)_12%,transparent)_1px,transparent_1.5px)] bg-[length:15px_15px]" />;
    $[22] = t16;
  } else {
    t16 = $[22];
  }
  let t17;
  if ($[23] !== embedded) {
    t17 = embedded ? <div data-testid="early-access-gate-static-mark" className="absolute inset-0 bg-[radial-gradient(circle,var(--foreground-brand)_1px,transparent_1.5px)] bg-[length:15px_15px] opacity-80 [mask-image:url('/assets/o-mark-white.svg')] [mask-position:center] [mask-repeat:no-repeat] [mask-size:min(72vw,42rem)] [-webkit-mask-image:url('/assets/o-mark-white.svg')] [-webkit-mask-position:center] [-webkit-mask-repeat:no-repeat] [-webkit-mask-size:min(72vw,42rem)]" /> : null;
    $[23] = embedded;
    $[24] = t17;
  } else {
    t17 = $[24];
  }
  let t18;
  if ($[25] !== animationReady || $[26] !== embedded) {
    t18 = animationReady ? <GateAnimationIframe iframeRef={iframeRef} src={buildBackgroundSrc(embedded)} embedded={embedded} onLoad={() => {
      postPlaybackToIframe(iframeRef.current, isInViewRef.current);
    }} /> : null;
    $[25] = animationReady;
    $[26] = embedded;
    $[27] = t18;
  } else {
    t18 = $[27];
  }
  let t19;
  if ($[28] !== embedded) {
    t19 = embedded ? <><div className="absolute inset-y-0 left-0 w-20 bg-gradient-to-r from-background-secondary to-transparent md:w-28" /><div className="absolute inset-y-0 right-0 w-20 bg-gradient-to-l from-background-secondary to-transparent md:w-28" /><div className="absolute inset-x-0 top-0 h-12 bg-gradient-to-b from-background-secondary to-transparent md:h-20" /><div className="absolute inset-x-0 bottom-0 h-12 bg-gradient-to-t from-background-secondary to-transparent md:h-20" /></> : <><div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center_top,transparent_0%,transparent_42%,color-mix(in_oklch,var(--background-secondary)_55%,transparent)_78%,var(--background-secondary)_100%)] md:bg-[radial-gradient(ellipse_at_center_top,transparent_0%,var(--background-secondary)_70%)]" /><div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,transparent_0%,transparent_48%,color-mix(in_oklch,var(--background-secondary)_65%,transparent)_82%,var(--background-secondary)_100%)] md:bg-[radial-gradient(ellipse_at_center,transparent_35%,var(--background-secondary)_100%)]" /></>;
    $[28] = embedded;
    $[29] = t19;
  } else {
    t19 = $[29];
  }
  let t20;
  if ($[30] !== t17 || $[31] !== t18 || $[32] !== t19) {
    t20 = <div ref={hostRef} className="pointer-events-none absolute inset-0 overflow-hidden bg-background-secondary" aria-hidden="true">{t16}{t17}{t18}{t19}</div>;
    $[30] = t17;
    $[31] = t18;
    $[32] = t19;
    $[33] = t20;
  } else {
    t20 = $[33];
  }
  return t20;
}